import { cn } from "@gigpro/ui/tailwind/cn";
import { Checkbox as CheckboxPrimitive } from "radix-ui";
import React from "react";
import { Check } from "../../Icons";

type CheckboxGroupContextValue = {
  value: Array<string>;
  onValueChange: (value: Array<string>) => void;
};
const CheckboxGroupContext = React.createContext<CheckboxGroupContextValue>({
  value: [],
  onValueChange: () => {},
});
interface CheckboxGroupProps extends React.HTMLAttributes<HTMLDivElement>, CheckboxGroupContextValue {
  ref?: React.Ref<HTMLDivElement>;
}
const CheckboxGroup = ({ ref, className, value, onValueChange, ...props }: CheckboxGroupProps) => {
  const [internalValue, setInternalValue] = React.useState(value ?? []);
  const [prevValue, setPrevValue] = React.useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setInternalValue(value);
  }

  return (
    <CheckboxGroupContext.Provider
      value={{
        value: internalValue,
        onValueChange: (value) => {
          React.startTransition(() => onValueChange(value));
          setInternalValue(value);
        },
      }}
    >
      <div className={cn("grid gap-2", className)} {...props} ref={ref} />
    </CheckboxGroupContext.Provider>
  );
};
CheckboxGroup.displayName = "CheckboxGroup";

export interface CheckboxProps extends Omit<
  React.ComponentProps<typeof CheckboxPrimitive.Root>,
  "children" | "asChild"
> {
  asDiv?: boolean;
  value?: string;
}
const Checkbox = ({ ref, className, value, checked, onCheckedChange, asDiv, ...props }: CheckboxProps) => {
  const context = React.useContext(CheckboxGroupContext);
  const Wrapper = asDiv ? "div" : React.Fragment;
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "size-[18px] shrink-0 rounded-sm border-2 border-gray bg-white text-white hover:border-brand focus-visible:border-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:border-medium-gray disabled:bg-light-gray data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:disabled:border-medium-gray data-[state=checked]:disabled:bg-medium-gray",
        className,
      )}
      checked={context && value != null ? context.value.includes(value) : checked}
      onCheckedChange={(checked) => {
        onCheckedChange?.(checked);
        if (!context || !value) return;
        if (checked) context.onValueChange([...context.value, value]);
        else context.onValueChange(context.value.filter((v) => v !== value));
      }}
      asChild={asDiv}
      {...props}
    >
      <Wrapper>
        <CheckboxPrimitive.Indicator className={cn("flex size-full items-center justify-center text-current")}>
          <Check className="size-full" />
        </CheckboxPrimitive.Indicator>
      </Wrapper>
    </CheckboxPrimitive.Root>
  );
};
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, CheckboxGroup };
